import { StyleSheet, View } from "react-native";
import React, { useState } from "react";
import {
  AccountDetails,
  EventType,
  PlanPackageDetails,
  SimplePlanPackageDetails,
  SubscriptionDetails,
} from "features/types";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import { useAppSelector } from "hooks/useAppSelector";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { AccountStackParamList } from "navigation/types";
import PlanNameWithPaymentInterval from "screens/Account/components/PlanNameWithPaymentInterval";
import Button from "components/Button";
import { DisplayType } from "../types";
import { intersection, uniqBy } from "lodash";
import { Divider, Text } from "@ui-kitten/components";
import Dialog from "components/Dialog";
import toast from "utils/toast";
import dayjs from "dayjs";
import Spacer from "components/Spacer";
import { useAccount } from "hooks/useAccount";
import { useMutation } from "@tanstack/react-query";
import { cancelOrReinstateSubscriptionFetcher } from "queries/account.queries";
import { toAddNewPlan } from "utils/navigationUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getPlanType } from "utils/planPackageUtils";
import { getUserName } from "utils/userUtils";

interface MemberSubscriptionListProps {
  memberId: number;
  currentMemberSubscriptions: SubscriptionDetails[];
  setDisplay: React.Dispatch<React.SetStateAction<DisplayType>>;
  setSelectedSubscription: React.Dispatch<
    React.SetStateAction<SubscriptionDetails | null>
  >;
  setUpgradablePlans: React.Dispatch<
    React.SetStateAction<PlanPackageDetails[]>
  >;
}

interface RenderPlanDetailProps {
  accountSubscriptions: SubscriptionDetails[];
  subscription: SubscriptionDetails;
  plans: PlanPackageDetails[];
  onPlandetailsPress: (sub: SubscriptionDetails) => void;
  onCancelRenewalPress: (sub: SubscriptionDetails) => void;
  onUpgradeBtnPress: (upgradablePlans: PlanPackageDetails[]) => void;
}

const findAccountSubscriptionEventTypes = (
  uniqAccountSubscriptions: SubscriptionDetails[]
) => {
  return uniqAccountSubscriptions.reduce((eventTypes, currentSubscription) => {
    const newEventTypes = currentSubscription.expired
      ? []
      : currentSubscription.planPackage.eventTypes;
    return eventTypes.concat(newEventTypes);
  }, [] as EventType[]); // e.g. ['FOOTBALL','MBB','VOLLEYBALL']
};

const getUniqAccountSubscriptions = (account: AccountDetails) => {
  const validAccountSubscriptions = account.subscriptions.filter(
    (sub) =>
      filterIneligibleSubscriptions(sub) &&
      getPlanType(sub.planPackage) === "RESERVATION"
  );
  const uniqValidAccountSubscriptions = uniqBy(
    validAccountSubscriptions,
    "planPackage.id"
  );
  return uniqValidAccountSubscriptions;
};

const differenceOfTwoArrays = (arr1: any[], arr2: any[]) => {
  return arr1
    .filter((x) => !arr2.includes(x))
    .concat(arr2.filter((x) => !arr1.includes(x)));
};

const findUpgradablePlans = (
  planPackages: PlanPackageDetails[],
  accountSubscriptionEventTypes: EventType[],
  selectedPlanEventTypes: EventType[],
  selectedPlanPackage: SimplePlanPackageDetails
) => {
  // At this step, we know that selectedPlanPackage cannot be a Voucher plan
  // check https://github.com/FanRally/fanrally-core/issues/604 for reference
  const filteredPlans = planPackages.filter((plan) => {
    if (getPlanType(selectedPlanPackage) !== getPlanType(plan)) return false; // cannot upgrade reservation plan to bank, or vice versa
    if (intersection(plan.eventTypes, selectedPlanEventTypes).length > 0) {
      const differenceOfTwoEventTypes = differenceOfTwoArrays(
        plan.eventTypes,
        selectedPlanEventTypes
      );
      if (
        intersection(accountSubscriptionEventTypes, differenceOfTwoEventTypes)
          .length > 0
      ) {
        return false;
      }
      return Number(plan.fullPrice) > Number(selectedPlanPackage.fullPrice);
    }
    return false;
  });
  return filteredPlans;
};

const RenderPlanDetail: React.FC<RenderPlanDetailProps> = ({
  accountSubscriptions,
  subscription,
  plans,
  onPlandetailsPress,
  onCancelRenewalPress,
  onUpgradeBtnPress,
}) => {
  const {
    planPackage,
    billingInterval,
    monthlyPrice,
    member: { id },
  } = subscription;
  const { account } = useAccount();

  if (!account) return null;
  // find a list of plan packages that current selected subscription can upgrade to
  // 1. Find all unique account event types
  const uniqAccountSubscriptions = getUniqAccountSubscriptions(account);
  const accountSubscriptionEventTypes = findAccountSubscriptionEventTypes(
    uniqAccountSubscriptions
  );
  // 2.Find current selected plan's event types
  const selectedPlanEventTypes = subscription.planPackage.eventTypes;
  /**
   * 3. For plan in allPlans:
   *      if plan.eventTypes include current selected event types
   *        find difference of plan's even types and current selected event types
   *          if the differences exist in account event types
   *            return false
   *          else
   *            return plan's fullPrice is more expensive than current selected plan's fullPrice
   *
   * 4. return filtered plans
   */
  const upgradablePlans = findUpgradablePlans(
    plans,
    accountSubscriptionEventTypes,
    selectedPlanEventTypes,
    planPackage
  );

  const isNotVoucherPlan = getPlanType(planPackage) !== "VOUCHER";

  const navigation = useNavigation<NavigationProp<AccountStackParamList>>();

  // returns true if account has some subscription that's canceled and not expired
  const accountHasCancelledSubscription = accountSubscriptions.some(
    (sub) => sub.status === "CANCELLED" && sub.expired === false
  );

  return (
    <View>
      {subscription.status === "CANCELLED" && (
        <Text status="danger">
          Plan expiration date:{" "}
          {dayjs(subscription.expiresAt).format("ddd MMM DD YYYY")}
        </Text>
      )}
      <PlanNameWithPaymentInterval
        planPackage={planPackage}
        billingInterval={billingInterval}
        monthlyPrice={monthlyPrice}
      />
      <BaseCardListItem
        title="Plan details"
        onPress={() => onPlandetailsPress(subscription)}
      />

      {planPackage.billingIntervals.length > 1 && // if plan package supports pay in full
        billingInterval === "MONTHLY" && // if current subscription is paid monthly
        subscription.status === "ACTIVE" && // if subscription is active
        accountHasCancelledSubscription === false && (
          <BaseCardListItem
            testID="change-payment-interval-button"
            title="Change payment interval"
            onPress={() =>
              navigation.navigate("UpdateBillingInterval", {
                pid: String(planPackage.id),
                mid: String(id),
              })
            }
          />
        )}

      {planPackage.cancelDisabled === false && ( // only if subscription is paid monthly and cancelDisabled flag is false, we allow user to cancel renewal
        <BaseCardListItem
          testID="cancelOrReinstate-button"
          title={
            subscription.status === "ACTIVE"
              ? "Cancel renewal"
              : "Reinstate renewal"
          }
          onPress={() => onCancelRenewalPress(subscription)}
        />
      )}

      {upgradablePlans.length > 0 &&
        isNotVoucherPlan &&
        subscription.status === "ACTIVE" && (
          <>
            <Divider style={{ marginVertical: 15 }} />
            <Button
              onPress={() => onUpgradeBtnPress(upgradablePlans)}
              testID="upgrade-plan-button"
            >
              Upgrade Available
            </Button>
          </>
        )}
    </View>
  );
};

const MemberSubscriptionList: React.FC<MemberSubscriptionListProps> = ({
  currentMemberSubscriptions,
  setDisplay,
  setSelectedSubscription,
  setUpgradablePlans,
  memberId,
}) => {
  const [planDetailDialog, setPlanDetailDialog] = useState(false);
  const [cancelRenewalDialog, setCancelRenewalDialog] = useState(false);
  const [currentSelectedSub, setCurrentSelectedSub] =
    useState<SubscriptionDetails | null>(null);

  const { account, fetchAccount } = useAccount();
  const { mutateAsync: cancelOrReinstate, isLoading: loading } = useMutation(
    cancelOrReinstateSubscriptionFetcher
  );
  const { allPlanPackages } = useAppSelector((state) => state.buyFlow);

  const handlePlandetailsPress = (sub: SubscriptionDetails) => {
    setCurrentSelectedSub(sub);
    setPlanDetailDialog(true);
  };

  const handleCancelRenewalPress = (sub: SubscriptionDetails) => {
    setCurrentSelectedSub(sub);
    setCancelRenewalDialog(true);
  };

  const isCurrentSubActive = currentSelectedSub?.status === "ACTIVE" || false;

  const handleCancelOrReinstateSubscription = async () => {
    if (!currentSelectedSub || !account) return;
    await cancelOrReinstate({
      accountId: account!.id,
      subscriptionId: currentSelectedSub.id,
      type: isCurrentSubActive ? "cancel" : "reinstate",
    });
    fetchAccount();
    setCancelRenewalDialog(false);
    toast.success(
      `You have ${isCurrentSubActive ? "cancelled" : "reinstated"} ${
        currentSelectedSub.planPackage.name
      } for ${getUserName(currentSelectedSub.member)}.`
    );
  };

  const getReservationWindows = (subscription: SubscriptionDetails) => {
    const reservationWindowHours =
      subscription.planPackage.primaryComponent.reservationWindowHours;
    if (reservationWindowHours < 0) {
      return "Reserve any time";
    }
    const days = reservationWindowHours / 24;
    return `${days}-day`;
  };

  const getMaxVouchersPerGame = (subscription: SubscriptionDetails) => {
    const perEventMax = subscription.planPackage.primaryComponent.perEventMax;
    return perEventMax < 0 ? "Unlimited" : perEventMax;
  };

  return (
    <View>
      {currentMemberSubscriptions.map((sub) => (
        <View key={sub.id}>
          <BaseCard>
            <RenderPlanDetail
              accountSubscriptions={account?.subscriptions || []}
              subscription={sub}
              plans={allPlanPackages}
              onPlandetailsPress={handlePlandetailsPress}
              onCancelRenewalPress={handleCancelRenewalPress}
              onUpgradeBtnPress={(upgradablePlans) => {
                setUpgradablePlans(upgradablePlans);
                setDisplay("UPGRADE");
                setSelectedSubscription(sub);
              }}
            ></RenderPlanDetail>
          </BaseCard>
          <Spacer />
        </View>
      ))}

      <BaseCard title="Add Plan to This Member">
        <BaseCardListItem
          title="Add new plan"
          onPress={() => toAddNewPlan(String(memberId))}
        />
      </BaseCard>

      <Spacer />

      {currentSelectedSub !== null && (
        <>
          {/* plan detail dialog */}
          <Dialog
            visible={planDetailDialog}
            setVisible={setPlanDetailDialog}
            cancelText="Cancel"
          >
            <BaseCard title={`${currentSelectedSub.planPackage.name} details`}>
              <BaseCardListItem
                title={`Reservation window: ${getReservationWindows(
                  currentSelectedSub
                )}`}
                showArrow={false}
              />
              <BaseCardListItem
                title={`Status: ${currentSelectedSub.status}`}
                showArrow={false}
              />

              {getPlanType(currentSelectedSub.planPackage) === "VOUCHER" && (
                <BaseCardListItem
                  title={`Max vouchers per game: ${getMaxVouchersPerGame(
                    currentSelectedSub
                  )}`}
                  showArrow={false}
                />
              )}
            </BaseCard>
          </Dialog>
          {/* cancel/reinstate subscription dialog */}
          <Dialog
            visible={cancelRenewalDialog}
            setVisible={setCancelRenewalDialog}
            cancelText="Cancel"
            okText="Confirm"
            onOk={handleCancelOrReinstateSubscription}
            okBtnProps={{ loading, testID: "cancelOrReinstate-confirm-button" }}
          >
            <Text category="h6">
              {isCurrentSubActive ? "Cancel" : "Reinstate"} Membership
            </Text>

            <Spacer margin={5} />
            <Text>
              You are {isCurrentSubActive ? "cancelling" : "reinstating"}{" "}
              renewal for:
            </Text>
            <Text style={styles.withMarginAndBold}>
              {currentSelectedSub.planPackage.name}
            </Text>
            <Text>for member</Text>
            <Text style={styles.withMarginAndBold}>
              {getUserName(currentSelectedSub.member)}
            </Text>
            {isCurrentSubActive && (
              <Text>
                your membership will remain active until the next renewal
                period.
              </Text>
            )}
          </Dialog>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  withMarginAndBold: { marginVertical: 5, fontWeight: "700" },
});
export default MemberSubscriptionList;
